import { PasswordInputRenderProps } from "@common";
import { useMediaQuery, Typography, useTheme } from "@mui/material";
import { Fade, StyledButton, StyledTextField } from "@natklok/components";
import { LangContext } from "@natklok/contexts/langContext";
import { RenderTemplateContext } from "@natklok/contexts/renderTemplateContext";
import { SelectAnswerContext } from "@natklok/contexts/selectAnswerContext";
import useShowingCorrectAnswer from "@natklok/hooks/useShowingCorrectAnswer";
import { getButtonPalletFromCorrectOrIncorrect } from "@natklok/util/getButtonPalletFromCorrectOrIncorrect";
import { useContext, useState } from "react";

import getPwdSecurity from "@natklok/util/getPwdSecurity";

interface RenderProps extends PasswordInputRenderProps {
  readOnly?: boolean;
  showCorrect?: boolean;
}

export const TestPasswordStrength: React.FC<{
  [key in keyof RenderProps]: RenderProps[key];
}> = ({
  profileBasedServiceName,
  profileBasedServiceColor,
  profileBasedServiceSecondColor,
  profileBasedServiceThirdColor,
  readOnly = false,
  showCorrect = false,
}) => {
  const { translatePageText } = useContext(LangContext);
  const { overrideQuestionSlideTitle, overrideQuestionSlideDesc } =
    useContext(RenderTemplateContext);
  const { onSelectAnswer } = useContext(SelectAnswerContext);
  const [password, setPassword] = useState("");
  const [showSecond, setShowSecond] = useState(false);
  const [pwdIsSecure, setPwdIsSecure] = useState(false);
  const showingCorrectAnswer = useShowingCorrectAnswer();
  const displayCorrectAnswers = showCorrect || showingCorrectAnswer;

  const handleSubmit = (score: number) => {
    if (score > 0.8) {
      onSelectAnswer("correct", { val1: 0, val2: 1, val3: 0 });
    } else {
      onSelectAnswer("incorrect", { val1: 0, val2: 0, val3: 1 });
    }
  };

  const handleProceedToAnswerOptions = () => {
    overrideQuestionSlideTitle(
      pwdIsSecure
        ? translatePageText("questions.passwordCheck.secondTitle").replace("{password}", password)
        : translatePageText("questions.passwordCheck.secondTitleUnsecurePwd")
    );
    overrideQuestionSlideDesc("");
    setShowSecond(true);
  };

  if (showSecond) {
    return (
      <Fade>
        <StyledButton
          hasCooldown
          onClick={() => handleSubmit(1)}
          fullWidth
          style={{ margin: "4px 0" }}
          label={translatePageText("questions.passwordCheck.option1")}
          clickable={displayCorrectAnswers === false}
          palette={getButtonPalletFromCorrectOrIncorrect(displayCorrectAnswers, "correct")}
        />
        <StyledButton
          hasCooldown
          onClick={() => handleSubmit(0.6)}
          fullWidth
          style={{ margin: "4px 0" }}
          label={translatePageText("questions.passwordCheck.option2")}
          clickable={displayCorrectAnswers === false}
          palette={getButtonPalletFromCorrectOrIncorrect(displayCorrectAnswers, "incorrect")}
        />
        <StyledButton
          hasCooldown
          onClick={() => handleSubmit(0)}
          fullWidth
          style={{ margin: "4px 0" }}
          label={translatePageText("questions.passwordCheck.option3")}
          clickable={displayCorrectAnswers === false}
          palette={getButtonPalletFromCorrectOrIncorrect(displayCorrectAnswers, "incorrect")}
        />
      </Fade>
    );
  } else {
    return (
      <div>
        <PwdSecurityModal
          profileBasedServiceName={profileBasedServiceName}
          profileBasedServiceColor={profileBasedServiceColor}
          profileBasedServiceSecondColor={profileBasedServiceSecondColor}
          profileBasedServiceThirdColor={profileBasedServiceThirdColor}
          setPassword={setPassword}
          setPwdIsSecure={setPwdIsSecure}
          readOnly={readOnly}
        />
        <StyledButton
          hasCooldown
          style={{ margin: "20px auto", display: "block" }}
          disabled={password.length > 0 ? false : readOnly ? false : true}
          onClick={handleProceedToAnswerOptions}
          label={translatePageText("test.nextQuestion")}
        />
      </div>
    );
  }
};

const PwdSecurityModal: React.FC<{
  profileBasedServiceName: string;
  profileBasedServiceColor: string;
  profileBasedServiceSecondColor: string;
  profileBasedServiceThirdColor: string;
  setPassword: (val: string) => void;
  setPwdIsSecure: (bool: boolean) => void;
  readOnly?: boolean;
}> = ({
  profileBasedServiceName,
  profileBasedServiceColor,
  profileBasedServiceSecondColor,
  profileBasedServiceThirdColor,
  setPassword,
  setPwdIsSecure,
  readOnly = false,
}) => {
  const { translatePageText } = useContext(LangContext);
  const [pwd, setPwd] = useState("");
  const [pwdTime, setPwdTime] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const translateToSwedish = (time: any) => {
    time = time.replace("pico", "piko");
    time = time.replace("micro", "mikro");
    time = time.replace("hundred", "hundra");
    time = time.replace("thousand", "tusen");
    time = time.replace("million", "miljoner");
    time = time.replace("billion", "miljarder");
    time = time.replace("Instantly", "0 sekunder");

    time = time.replace("seconds", "sekunder");
    time = time.replace("second", "sekund");

    time = time.replace("minutes", "minuter");
    if (time.includes("minuter") === false) {
      time = time.replace("minute", "minut");
    }

    time = time.replace("hours", "timmar");
    time = time.replace("hour", "timme");

    time = time.replace("days", "dagar");
    time = time.replace("day", "dag");

    time = time.replace("weeks", "veckor");
    time = time.replace("week", "vecka");

    time = time.replace("months", "månader");
    time = time.replace("month", "månad");

    time = time.replace("years", "år");
    time = time.replace("year", "år");

    return time;
  };

  const onChange = (e: any) => {
    const res = getPwdSecurity(e.target.value);
    setPassword(e.target.value);
    setPwd(e.target.value);
    setPwdTime(translateToSwedish(res.time));
    setPwdIsSecure(res.time.includes("years"));
  };

  return (
    <div
      style={{
        background: profileBasedServiceColor,
        padding: isMobile ? "6px 6px 2px 6px" : 25,
        borderRadius: 8,
        position: "relative",
        boxShadow: "0px 0px 10px 0px rgba(0,0,0,0.2)",
      }}
    >
      <span
        style={{
          textTransform: "uppercase",
          fontWeight: "bold",
          fontFamily: "sans-serif",
          letterSpacing: -3,
          display: "inline-block",
          marginBottom: isMobile ? 0 : 10,
          fontSize: 24,
          color: profileBasedServiceThirdColor,
        }}
      >
        {profileBasedServiceName}
      </span>
      <span
        style={{
          marginLeft: 10,
          color: profileBasedServiceThirdColor,
        }}
      >
        Allt samlat.
      </span>
      <div style={{ position: "relative" }}>
        <StyledTextField
          onChange={onChange}
          margin="none"
          fullWidth
          autoFocus
          label={translatePageText("questions.passwordCheck.inputPlaceholder")}
          color={profileBasedServiceThirdColor}
          disabled={readOnly}
        />
      </div>
      <div
        style={{
          borderRadius: 5,
          background: profileBasedServiceThirdColor,
          color: profileBasedServiceColor,
          transition: "0.3s ease-in-out",
          marginTop: isMobile ? 4 : 20,
          padding: "6px 0",
          border: "none",
          textAlign: "center",
          width: "100%",
          display: pwd === "" ? "none" : "inline-block",
        }}
      >
        <Typography style={{ margin: 0, opacity: "0.8" }} variant="body2">
          {translatePageText("questions.passwordCheck.resultPrefix")}
        </Typography>
        <Typography
          style={{
            margin: "5px 0",
            display: "block",
            lineHeight: 1,
          }}
          variant="h6"
          component="span"
        >
          {pwdTime}
        </Typography>
        <Typography style={{ margin: 0, opacity: "0.8" }} variant="body2">
          {translatePageText("questions.passwordCheck.resultSuffix")}
        </Typography>
      </div>
      <Typography
        style={{
          display: "block",
          marginTop: 9,
          marginBottom: 9,
          textAlign: "center",
          color: profileBasedServiceThirdColor,
          lineHeight: 1,
        }}
        variant="caption"
      >
        Använder samma teknologi som howsecureismypassword.net
      </Typography>
    </div>
  );
};
